import { IconChevronLeft, IconChevronRight, IconX } from '@tabler/icons-react';
import { useEffect, useRef, useState } from 'react';
import styles from './resumeJourneyCarousel.module.scss';
import AnimatedDiv from '@/components/animated/animated';
import { CarouselProps } from './corouselType';
import { debounce } from '@/core/utils/general.utils';
import { H6 } from '@/design-system/text/h6/h6';
import { CaptionM } from '@/design-system/text/caption-m/caption-m';
import { Caption } from '@/design-system/text/caption/caption';

export default function ResumeJourneyCarousel<T>({
  cards,
  title,
  Component,
  onCrossClick,
  autoPlay,
  autoplayInterval = 4000,
}: CarouselProps<T>) {
  const [currentIndex, setCurrentIndex] = useState(1);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const carouselRef = useRef<HTMLDivElement>(null);
  const autoplayRef = useRef<NodeJS.Timeout | null>(null);
  const handlePrevClick = debounce(() => {
    if (!isTransitioning) {
      setCurrentIndex((prevIndex) => prevIndex - 1);
      setIsTransitioning(true);
    }
  }, 50);
  const handleNextClick = debounce(() => {
    if (!isTransitioning) {
      setCurrentIndex((prevIndex) => prevIndex + 1);
      setIsTransitioning(true);
    }
  }, 50);
  const handleTransitionEnd = () => {
    if (carouselRef.current) {
      setIsTransitioning(false);
      if (currentIndex === 0) {
        setCurrentIndex(cards.length);
        carouselRef.current.style.transition = 'none';
        carouselRef.current.style.transform = `translateX(-${cards.length * 100}%)`;
      } else if (currentIndex >= cards.length + 1) {
        setCurrentIndex(1);
        carouselRef.current.style.transition = 'none';
        carouselRef.current.style.transform = `translateX(-100%)`;
      }
    }
  };
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (!isTransitioning && carouselRef.current) {
        carouselRef.current.style.transition = 'transform 0.5s ease';
      }
    }, 50);

    return () => clearTimeout(timeoutId);
  }, [isTransitioning]);
  useEffect(() => {
    const startAutoplay = () => {
      if (autoPlay && cards.length > 1) {
        autoplayRef.current = setInterval(() => {
          handleNextClick();
        }, autoplayInterval);
      }
    };
    const stopAutoplay = () => {
      if (autoplayRef.current) {
        clearInterval(autoplayRef.current);
      }
    };
    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopAutoplay();
      } else {
        startAutoplay();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    startAutoplay();
    return () => {
      stopAutoplay();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [autoPlay, autoplayInterval, cards, handleNextClick]);
  if (cards.length < 1) {
    return null;
  }
  const displayIndex =
    currentIndex === 0
      ? cards.length
      : currentIndex >= cards.length + 1
        ? 1
        : currentIndex;
  return (
    <div className={styles['main-con']}>
      {onCrossClick && (
        <div
          className={`${styles['cross-con']} ${isTransitioning ? styles['hidden'] : ''}`}
          onClick={() => onCrossClick(displayIndex - 1)}
        >
          <IconX size={16} color="var(--primary-midnight)" />
        </div>
      )}
      <div className={styles['header-con']}>
        <div className={styles['text-header-con']}>
          <H6 text={title} />
          {cards.length > 1 && (
            <AnimatedDiv.BottomToTopWithOpacity
              key={cards.length}
              className={styles['indicator-con']}
            >
              <CaptionM
                text={displayIndex.toString()}
                className={styles['active-indicator']}
              ></CaptionM>
              <Caption
                text={'/' + cards.length.toString()}
                className={styles['indicator']}
              />
            </AnimatedDiv.BottomToTopWithOpacity>
          )}
        </div>
        {cards.length > 1 && (
          <div className={styles['chevron-con']}>
            <IconChevronLeft
              size={20}
              color={'var(--primary-color)'}
              onClick={handlePrevClick}
              className={styles['chevron-icon']}
            />
            <IconChevronRight
              size={20}
              color={'var(--primary-color)'}
              onClick={handleNextClick}
              className={styles['chevron-icon']}
            />
          </div>
        )}
      </div>
      <div className={styles['carousel-container']}>
        <div
          className={styles['carousel-con']}
          ref={carouselRef}
          style={{ transform: `translateX(-${currentIndex * 100}%)` }}
          onTransitionEnd={handleTransitionEnd}
        >
          <div className={styles['carousel-card']}>
            <Component key={cards.length - 1} {...cards[cards.length - 1]} />
          </div>
          {cards.map((card, index) => (
            <div key={index} className={styles['carousel-card']}>
              <Component key={index} {...card} />
            </div>
          ))}
          <div className={styles['carousel-card']}>
            <Component key={0} {...cards[0]} /> {/* Append first card */}
          </div>
        </div>
      </div>
    </div>
  );
}
